import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, Send, Plus, Trash2, Sparkles, 
  ChevronRight, X, Check,
  Building2, Lock, Briefcase, Users, Truck, Package,
  TrendingDown, UserCheck, Star, Container, LogOut,
  Download, CreditCard, GripVertical, AlertCircle, FileCheck
} from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/services/api';
import { toast } from 'sonner';
import { LogoFull } from '@/components/Logo';
import { OnboardingWizard, STORAGE_KEY as ONBOARDING_KEY } from '@/components/OnboardingWizard';

const ICON_MAP = {
  shield: Shield, lock: Lock, briefcase: Briefcase, building: Building2,
  'trending-down': TrendingDown, users: Users, 'user-check': UserCheck,
  star: Star, truck: Truck, package: Package, 'users-round': Users, container: Container
};

const SortableBlock = ({ block, onRemove, getIcon }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.block_id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };
  const IconComponent = getIcon(block.icon);

  return (
    <div ref={setNodeRef} style={style} className="coverage-block p-5 relative group" data-testid={`canvas-block-${block.coverage_type}`}>
      <div {...attributes} {...listeners} className="absolute top-3 left-3 p-1 rounded cursor-grab active:cursor-grabbing text-[#8DA0B9] hover:text-[#0D1B2A] opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`drag-handle-${block.block_id}`}>
        <GripVertical size={16} />
      </div>
      <button
        onClick={() => onRemove(block.block_id)}
        className="absolute top-3 right-3 p-1.5 rounded-md bg-[#E74C3C]/10 text-[#E74C3C] opacity-0 group-hover:opacity-100 transition-opacity"
        data-testid={`remove-block-${block.block_id}`}
      >
        <Trash2 size={14} />
      </button>
      <div className="flex items-start gap-3 mb-3 pl-5">
        <div className="w-10 h-10 bg-[#1A3A64]/10 rounded-lg flex items-center justify-center flex-shrink-0">
          <IconComponent size={20} className="text-[#1A3A64]" />
        </div>
        <div>
          <h4 className="font-medium text-[#0D1B2A]">{block.name}</h4>
          <p className="text-xs text-[#8DA0B9]">{block.coverage_type.replace(/_/g, ' ')}</p>
        </div>
      </div>
      <p className="text-sm text-[#3D5A80] mb-4 line-clamp-2">{block.description}</p>
      <div className="flex items-center justify-between pt-3 border-t border-[#DCE4ED]">
        <div>
          <p className="text-xs text-[#8DA0B9]">Coverage</p>
          <p className="font-medium text-[#0D1B2A]">${(block.coverage_limit || 0).toLocaleString()}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-[#8DA0B9]">Premium</p>
          <p className="font-semibold text-[#1A3A64]">${block.base_premium}/mo</p>
        </div>
      </div>
    </div>
  );
};

const PolicyBuilder = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const [policy, setPolicy] = useState(null);
  const [coverageBlocks, setCoverageBlocks] = useState([]);
  const [canvasBlocks, setCanvasBlocks] = useState([]);
  const [chatMessages, setChatMessages] = useState([
    { role: 'assistant', content: "Hi! I'm your insurance advisor. Tell me about your business—what do you do, how many employees, what are your main concerns? I'll help you build the perfect coverage." }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [suggestedBlocks, setSuggestedBlocks] = useState([]);
  const [showBlockLibrary, setShowBlockLibrary] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [coiLoading, setCoiLoading] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(() => !localStorage.getItem(ONBOARDING_KEY));

  const chatEndRef = useRef(null);

  useEffect(() => {
    const init = async () => {
      try {
        const blocks = await api.getCoverageBlocks();
        setCoverageBlocks(blocks);
        const policies = await api.getPolicies();
        if (policies.length > 0) {
          const existingPolicy = policies.find(p => p.status === 'draft') || policies[0];
          setPolicy(existingPolicy);
          setCanvasBlocks(existingPolicy.coverage_blocks || []);
        } else {
          const newPolicy = await api.createPolicy();
          setPolicy(newPolicy);
        }
      } catch (error) {
        console.error('Init error:', error);
      }
    };
    init();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!chatInput.trim() || chatLoading) return;
    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);
    try {
      const response = await api.sendChatMessage(userMessage, sessionId, policy?.policy_id);
      setSessionId(response.session_id);
      setChatMessages(prev => [...prev, { role: 'assistant', content: response.response, suggestedBlocks: response.suggested_blocks }]);
      if (response.suggested_blocks?.length > 0) setSuggestedBlocks(response.suggested_blocks);
    } catch {
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
    } finally {
      setChatLoading(false);
    }
  };

  const addBlockToCanvas = async (block) => {
    if (!policy || canvasBlocks.some(b => b.coverage_type === block.coverage_type)) return;
    try {
      const updated = await api.addBlockToPolicy(policy.policy_id, {
        coverage_type: block.coverage_type,
        position: { x: 50 + (canvasBlocks.length % 3) * 220, y: 50 + Math.floor(canvasBlocks.length / 3) * 180 }
      });
      setPolicy(updated);
      setCanvasBlocks(updated.coverage_blocks || []);
      setSuggestedBlocks(prev => prev.filter(b => b.coverage_type !== block.coverage_type));
    } catch (error) {
      console.error('Add block error:', error);
    }
  };

  const removeBlockFromCanvas = async (blockId) => {
    if (!policy) return;
    try {
      await api.removeBlockFromPolicy(policy.policy_id, blockId);
      setCanvasBlocks(prev => prev.filter(b => b.block_id !== blockId));
      const updated = await api.getPolicy(policy.policy_id);
      setPolicy(updated);
    } catch (error) {
      console.error('Remove block error:', error);
    }
  };

  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !policy) return;

    const oldIndex = canvasBlocks.findIndex(b => b.block_id === active.id);
    const newIndex = canvasBlocks.findIndex(b => b.block_id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const newBlocks = [...canvasBlocks];
    const [moved] = newBlocks.splice(oldIndex, 1);
    newBlocks.splice(newIndex, 0, moved);
    setCanvasBlocks(newBlocks);

    const positions = {};
    newBlocks.forEach((b, i) => {
      positions[b.block_id] = { x: (i % 3) * 220 + 50, y: Math.floor(i / 3) * 180 + 50 };
    });

    try {
      await api.updateBlockPositions(policy.policy_id, positions);
    } catch (err) {
      console.error('Position save error:', err);
    }
  }, [canvasBlocks, policy]);

  const handleCheckout = async (billingCycle) => {
    if (!policy) return;
    setCheckoutLoading(true);
    try {
      const result = await api.createCheckoutSession(policy.policy_id, billingCycle);
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (error) {
      toast.error('Failed to create checkout session');
      console.error('Checkout error:', error);
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!policy) return;
    setPdfLoading(true);
    try {
      const blob = await api.downloadPolicyPdf(policy.policy_id);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `InsureLab_Policy_${policy.policy_id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch {
      toast.error('Failed to generate PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleDownloadCOI = async () => {
    if (!policy) return;
    setCoiLoading(true);
    try {
      await api.downloadCOI(policy.policy_id);
      toast.success('Certificate of Insurance downloaded');
    } catch {
      toast.error('Failed to generate COI');
    } finally {
      setCoiLoading(false);
    }
  };

  const totalMonthly = canvasBlocks.reduce((sum, b) => sum + (b.base_premium || 0), 0);
  const totalAnnual = totalMonthly * 12 * 0.9;
  const totalCoverage = canvasBlocks.reduce((sum, b) => sum + (b.coverage_limit || 0), 0);
  const getIcon = (iconName) => ICON_MAP[iconName] || Shield;

  return (
    <div className="h-screen flex flex-col bg-[#F0F4F8]" data-testid="policy-builder">
      {/* Onboarding Wizard */}
      {showOnboarding && <OnboardingWizard onComplete={() => setShowOnboarding(false)} />}
      {/* Header */}
      <header className="h-16 bg-white border-b border-[#DCE4ED] flex items-center justify-between px-6 flex-shrink-0">
        <Link to="/" className="flex items-center gap-2">
          <LogoFull size="small" />
        </Link>
        <div className="flex items-center gap-4">
          <Link to="/claims" className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#DCE4ED] text-sm text-[#3D5A80] hover:border-[#1A3A64] hover:text-[#1A3A64] transition-colors" data-testid="nav-claims">
            <AlertCircle size={14} />
            Claims
          </Link>
          <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-[#00AEEF]/10 rounded-full">
            <Sparkles size={14} className="text-[#00AEEF]" />
            <span className="text-sm font-medium text-[#00AEEF]">AI-Assisted</span>
          </div>
          <span className="text-sm text-[#3D5A80]">{user?.full_name}</span>
          <Button variant="ghost" size="icon" onClick={() => { logout(); navigate('/'); }} data-testid="logout-btn">
            <LogOut size={18} />
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Canvas Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Canvas Toolbar */}
          <div className="h-14 bg-white border-b border-[#DCE4ED] flex items-center justify-between px-6">
            <div className="flex items-center gap-4">
              <h2 className="font-medium text-[#0D1B2A]">Policy Canvas</h2>
              <span className="text-sm text-[#8DA0B9]">{canvasBlocks.length} coverage{canvasBlocks.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="flex items-center gap-2">
              {canvasBlocks.length > 0 && (
                <>
                  <Button variant="outline" size="sm" className="border-[#DCE4ED]" onClick={handleDownloadPdf} disabled={pdfLoading} data-testid="download-pdf-btn">
                    <Download size={16} className="mr-2" />
                    {pdfLoading ? 'Generating...' : 'PDF'}
                  </Button>
                  <Button variant="outline" size="sm" className="border-[#00AEEF]/30 text-[#00AEEF] hover:bg-[#00AEEF]/5" onClick={handleDownloadCOI} disabled={coiLoading} data-testid="download-coi-btn">
                    <FileCheck size={16} className="mr-2" />
                    {coiLoading ? 'Generating...' : 'COI'}
                  </Button>
                </>
              )}
              <Button variant="outline" size="sm" className="border-[#DCE4ED]" onClick={() => setShowBlockLibrary(true)} data-testid="add-coverage-btn">
                <Plus size={16} className="mr-2" />
                Add Coverage
              </Button>
            </div>
          </div>

          {/* Canvas */}
          <div className="flex-1 canvas-bg overflow-auto p-6 relative">
            {canvasBlocks.length === 0 ? (
              <div className="h-full flex items-center justify-center">
                <div className="text-center max-w-md">
                  <div className="w-16 h-16 bg-[#DCE4ED] rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Plus size={32} className="text-[#8DA0B9]" />
                  </div>
                  <h3 className="text-xl font-medium text-[#0D1B2A] mb-2">Start Building Your Policy</h3>
                  <p className="text-[#3D5A80] mb-6">
                    Describe your business in the chat, and I'll suggest the right coverage blocks. Or browse the library to add them manually.
                  </p>
                  <Button onClick={() => setShowBlockLibrary(true)} className="btn-ai" data-testid="browse-library-btn">
                    <Sparkles size={16} className="mr-2" />
                    Browse Coverage Library
                  </Button>
                </div>
              </div>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={canvasBlocks.map(b => b.block_id)} strategy={rectSortingStrategy}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {canvasBlocks.map(block => (
                      <SortableBlock key={block.block_id} block={block} onRemove={removeBlockFromCanvas} getIcon={getIcon} />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}

            {/* Floating Pricing Widget */}
            {canvasBlocks.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="fixed bottom-6 left-1/2 -translate-x-1/2 lg:left-auto lg:translate-x-0 lg:right-[340px] pricing-widget px-6 py-4 flex items-center gap-6"
              >
                <div>
                  <p className="text-xs text-[#8DA0B9] uppercase tracking-wider">Total Coverage</p>
                  <p className="text-lg font-semibold text-[#0D1B2A]">${totalCoverage.toLocaleString()}</p>
                </div>
                <div className="w-px h-10 bg-[#DCE4ED]" />
                <div>
                  <p className="text-xs text-[#8DA0B9] uppercase tracking-wider">Monthly Premium</p>
                  <p className="text-2xl font-semibold text-[#1A3A64]">${totalMonthly}<span className="text-sm font-normal">/mo</span></p>
                </div>
                <Button className="btn-primary ml-2" onClick={() => setShowCheckout(true)} data-testid="finalize-btn">
                  <CreditCard size={16} className="mr-1" />
                  Finalize Policy
                  <ChevronRight size={18} className="ml-1" />
                </Button>
              </motion.div>
            )}
          </div>
        </div>

        {/* AI Chat Sidebar */}
        <div className="w-[320px] lg:w-[380px] bg-white border-l border-[#DCE4ED] flex flex-col flex-shrink-0">
          <div className="h-14 border-b border-[#DCE4ED] flex items-center justify-between px-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#00AEEF]/20 rounded-full flex items-center justify-center">
                <Sparkles size={16} className="text-[#00AEEF]" />
              </div>
              <div>
                <p className="font-medium text-sm text-[#0D1B2A]">AI Advisor</p>
                <p className="text-xs text-[#8DA0B9]">Online</p>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {chatMessages.map((msg, index) => (
              <div key={index} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-4 py-3 ${msg.role === 'user' ? 'user-bubble' : 'ai-bubble'}`}>
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  {msg.suggestedBlocks?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-[#DCE4ED]/50 space-y-2">
                      <p className="text-xs text-[#8DA0B9] mb-2">Suggested coverage:</p>
                      {msg.suggestedBlocks.map((block, i) => (
                        <button key={i} onClick={() => addBlockToCanvas(block)} className="w-full text-left p-2 rounded-lg bg-white/50 hover:bg-white transition-colors flex items-center justify-between" data-testid={`suggested-${block.coverage_type}`}>
                          <div className="flex items-center gap-2">
                            <Plus size={14} className="text-[#1A3A64]" />
                            <span className="text-sm font-medium text-[#0D1B2A]">{block.name}</span>
                          </div>
                          <span className="text-xs text-[#1A3A64]">${block.base_premium}/mo</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {chatLoading && (
              <div className="flex justify-start">
                <div className="ai-bubble px-4 py-3">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-[#8DA0B9] rounded-full typing-dot" />
                    <span className="w-2 h-2 bg-[#8DA0B9] rounded-full typing-dot" />
                    <span className="w-2 h-2 bg-[#8DA0B9] rounded-full typing-dot" />
                  </div>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {suggestedBlocks.length > 0 && (
            <div className="px-4 py-2 border-t border-[#DCE4ED] bg-[#F0F4F8]">
              <p className="text-xs text-[#8DA0B9] mb-2">Quick add:</p>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {suggestedBlocks.map((block, i) => (
                  <button key={i} onClick={() => addBlockToCanvas(block)} className="flex-shrink-0 px-3 py-1.5 bg-white border border-[#DCE4ED] rounded-full text-xs font-medium text-[#0D1B2A] hover:border-[#00AEEF] transition-colors flex items-center gap-1">
                    <Plus size={12} />
                    {block.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <form onSubmit={sendMessage} className="p-4 pb-20 border-t border-[#DCE4ED]">
            <div className="flex gap-2">
              <Input value={chatInput} onChange={(e) => setChatInput(e.target.value)} placeholder="Describe your business..." className="flex-1 rounded-full border-[#DCE4ED] focus:border-[#00AEEF] focus:ring-[#00AEEF] px-4" disabled={chatLoading} data-testid="chat-input" />
              <Button type="submit" size="icon" className="rounded-full btn-primary" disabled={chatLoading || !chatInput.trim()} data-testid="chat-send-btn">
                <Send size={18} />
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Block Library Modal */}
      <AnimatePresence>
        {showBlockLibrary && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowBlockLibrary(false)}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-white rounded-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="p-6 border-b border-[#DCE4ED] flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-[#0D1B2A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Coverage Library</h2>
                  <p className="text-sm text-[#8DA0B9]">Click to add coverage blocks to your policy</p>
                </div>
                <button onClick={() => setShowBlockLibrary(false)} className="p-2 hover:bg-[#F0F4F8] rounded-lg"><X size={20} className="text-[#8DA0B9]" /></button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[calc(80vh-100px)]">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {coverageBlocks.map((block) => {
                    const isAdded = canvasBlocks.some(b => b.coverage_type === block.coverage_type);
                    const IconComponent = getIcon(block.icon);
                    return (
                      <motion.button key={block.coverage_type} whileHover={{ scale: 1.02 }} onClick={() => !isAdded && addBlockToCanvas(block)} className={`p-4 rounded-xl border text-left transition-all ${isAdded ? 'border-[#1A3A64] bg-[#1A3A64]/5 cursor-default' : 'border-[#DCE4ED] hover:border-[#00AEEF] hover:shadow-md cursor-pointer'}`} data-testid={`library-block-${block.coverage_type}`}>
                        <div className="flex items-start justify-between mb-2">
                          <div className="w-10 h-10 bg-[#1A3A64]/10 rounded-lg flex items-center justify-center"><IconComponent size={20} className="text-[#1A3A64]" /></div>
                          {isAdded && <span className="flex items-center gap-1 text-xs text-[#1A3A64] font-medium"><Check size={14} />Added</span>}
                        </div>
                        <h4 className="font-medium text-[#0D1B2A] mb-1">{block.name}</h4>
                        <p className="text-xs text-[#8DA0B9] line-clamp-2 mb-3">{block.description}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-[#8DA0B9]">${block.coverage_limit?.toLocaleString()} limit</span>
                          <span className="font-semibold text-[#1A3A64]">${block.base_premium}/mo</span>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Checkout Modal */}
      <AnimatePresence>
        {showCheckout && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowCheckout(false)}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-white rounded-2xl max-w-lg w-full overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="checkout-modal">
              <div className="p-6 border-b border-[#DCE4ED]">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-[#0D1B2A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Finalize Your Policy</h2>
                  <button onClick={() => setShowCheckout(false)} className="p-2 hover:bg-[#F0F4F8] rounded-lg"><X size={20} className="text-[#8DA0B9]" /></button>
                </div>
              </div>
              <div className="p-6">
                <div className="bg-[#F0F4F8] rounded-xl p-4 mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-[#3D5A80]">{canvasBlocks.length} coverage block{canvasBlocks.length !== 1 ? 's' : ''}</span>
                    <span className="text-sm text-[#8DA0B9]">Total: ${totalCoverage.toLocaleString()}</span>
                  </div>
                  <div className="space-y-1">
                    {canvasBlocks.map(b => (
                      <div key={b.block_id} className="flex items-center justify-between text-sm">
                        <span className="text-[#0D1B2A]">{b.name}</span>
                        <span className="text-[#1A3A64] font-medium">${b.base_premium}/mo</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => handleCheckout('monthly')}
                    disabled={checkoutLoading}
                    className="p-4 rounded-xl border-2 border-[#DCE4ED] hover:border-[#1A3A64] transition-all text-left group"
                    data-testid="checkout-monthly-btn"
                  >
                    <p className="text-xs text-[#8DA0B9] uppercase mb-1">Monthly</p>
                    <p className="text-2xl font-semibold text-[#0D1B2A] group-hover:text-[#1A3A64]">${totalMonthly.toFixed(2)}</p>
                    <p className="text-xs text-[#8DA0B9]">per month</p>
                  </button>
                  <button
                    onClick={() => handleCheckout('annual')}
                    disabled={checkoutLoading}
                    className="p-4 rounded-xl border-2 border-[#1A3A64] bg-[#1A3A64]/5 text-left relative overflow-hidden"
                    data-testid="checkout-annual-btn"
                  >
                    <span className="absolute top-2 right-2 px-1.5 py-0.5 bg-[#1A3A64] text-white text-[10px] font-bold rounded">SAVE 10%</span>
                    <p className="text-xs text-[#8DA0B9] uppercase mb-1">Annual</p>
                    <p className="text-2xl font-semibold text-[#1A3A64]">${totalAnnual.toFixed(2)}</p>
                    <p className="text-xs text-[#8DA0B9]">per year</p>
                  </button>
                </div>

                {checkoutLoading && (
                  <div className="mt-4 text-center text-sm text-[#3D5A80]">
                    <div className="animate-spin w-5 h-5 border-2 border-[#1A3A64] border-t-transparent rounded-full mx-auto mb-2" />
                    Redirecting to secure checkout...
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PolicyBuilder;
